// 🔐 API de Autenticación — vía RPC de PostgREST (/rpc/login, /rpc/me, ...)
import { apiClient, tokenStore } from './client';

export interface LoginCredentials {
  username: string;
  password: string;
  /**
   * Slug de la empresa (multi-tenant, Fase 2) para desambiguar cuando el mismo
   * username existe en varias empresas. Si se omite, el backend usa el fallback
   * global (solo entra si el username es único en todo el sistema).
   */
  tenant?: string;
  /** «Mantener la sesión en este dispositivo». undefined = comportamiento de siempre (ERP: se conserva). */
  recordar?: boolean;
}

export interface RegisterData {
  username: string;
  email: string;
  password: string;
  empresaId: string;
}

export interface Perfil {
  id: string;
  nombre: string;
  descripcion?: string;
  modo_app?: boolean;  // 733: interfaz mínima tipo app móvil (sin menú/encabezado)
  permisos: {
    usuarios?: {
      ver?: boolean;
      crear?: boolean;
      editar?: boolean;
      eliminar?: boolean;
    };
    empresas?: {
      ver?: boolean;
      crear?: boolean;
      editar?: boolean;
      eliminar?: boolean;
    };
    crud_factory?: {
      acceso?: boolean;
    };
    reportes?: {
      ver?: boolean;
      exportar?: boolean;
    };
    [key: string]: any; // Para permisos personalizados
  };
  nivel_acceso: number;
}

export interface User {
  id: string;
  username: string;
  email: string;
  perfil: Perfil;
  empresaId: string;
  empresa: {
    id: string;
    nombre: string;
    logo?: string;
  };
  activo?: boolean;
  mustChangePassword?: boolean;
  ultimoAcceso?: string;
}

export interface LoginResponse {
  message: string;
  user: User;
  token: string;
}

/**
 * Segundo paso (MFA, TrackConstruccion script 006): con MFA activo o exigido por el
 * perfil, /rpc/login NO entrega token sino un desafío de 5 minutos.
 * 'verificar' = pedir el código de la app; 'enrolar' = configurar la app primero.
 */
export interface MfaDesafio {
  mfa: 'verificar' | 'enrolar';
  mfaTicket: string;
}
export type LoginResultado = LoginResponse | MfaDesafio;
export const esDesafioMfa = (r: unknown): r is MfaDesafio => !!r && typeof r === 'object' && 'mfaTicket' in r;

/** Respuesta de los pasos MFA: sesión completa, o error sin excepción (el contador de intentos debe persistir). */
export interface MfaRespuesta extends Partial<LoginResponse> {
  refreshToken?: string;
  codigosRecuperacion?: string[];
  error?: 'mfa_codigo_invalido' | 'mfa_bloqueado' | 'mfa_ticket_vencido';
  intentosRestantes?: number;
}

/**
 * Branding de empresa para el login por slug (multi-tenant, Fase 1).
 */
export interface LoginBranding {
  nombre: string;
  logo: string | null;
  /** Fondo de pantalla completa del login (data URI), o null si la empresa no configuró uno. */
  fondo?: string | null;
}

/**
 * Igual que LoginBranding pero resuelto por el DOMINIO, no por la URL. Trae
 * además el slug porque el login lo necesita como `_tenant` (el host por sí
 * solo no viaja al backend: el tenant sigue siendo el slug de siempre).
 */
export interface LoginBrandingHost extends LoginBranding {
  slug: string;
}

// Memoria del ÚLTIMO tenant identificado en este dispositivo (solo el slug,
// NUNCA usuario ni contraseña). Se usa para volver a /login/<slug> tras
// expirar la sesión y para el atajo "Entrar a tu empresa" en /login.
export const LOGIN_SLUG_KEY = 'erp_login_slug';

export const loginSlugStore = {
  get: (): string | null => {
    try {
      return localStorage.getItem(LOGIN_SLUG_KEY);
    } catch {
      return null;
    }
  },
  set: (slug: string) => {
    try {
      localStorage.setItem(LOGIN_SLUG_KEY, slug);
    } catch {
      /* almacenamiento no disponible (modo privado, etc.) */
    }
  },
  clear: () => {
    try {
      localStorage.removeItem(LOGIN_SLUG_KEY);
    } catch {
      /* no-op */
    }
  },
};

// Elección «mantener la sesión» hecha en el formulario: se aplica cuando llega el token, que con
// MFA llega en el segundo paso (login_mfa / enrolar), no en /rpc/login.
let recordarPendiente: boolean | undefined;

export const authAPI = {
  /**
   * Login → /rpc/login. Guarda access + refresh token en el cliente.
   */
  login: async (credentials: LoginCredentials): Promise<LoginResultado> => {
    try {
      const { data } = await apiClient.post('/rpc/login', {
        _username: credentials.username,
        _password: credentials.password,
        _tenant: credentials.tenant ?? null,
      });
      recordarPendiente = credentials.recordar;
      if (!esDesafioMfa(data)) tokenStore.set(data.token, data.refreshToken, recordarPendiente);
      return data;
    } catch (e) {
      // El intento fallido no se puede registrar dentro de `login()`: la
      // excepción de credenciales revierte la transacción y se lleva por delante
      // cualquier registro. Se anota desde aquí, al recibir el rechazo. Es
      // best-effort a propósito —sirve para ver patrones de fuerza bruta, no
      // como evidencia irrefutable— y nunca puede romper el login.
      void apiClient
        .post('/rpc/registrar_acceso_fallido', { _username: credentials.username })
        .catch(() => undefined);
      throw e;
    }
  },

  /** MFA — segundo paso del login con el código de la app o uno de recuperación. */
  loginMfa: async (ticket: string, codigo: string): Promise<MfaRespuesta> => {
    const { data } = await apiClient.post('/rpc/login_mfa', { _ticket: ticket, _codigo: codigo });
    if (data?.token) tokenStore.set(data.token, data.refreshToken, recordarPendiente);
    return data;
  },
  /** MFA — enrolamiento exigido durante el login: secreto + URI otpauth para el QR. */
  mfaEnrolarIniciar: async (ticket: string): Promise<{ secreto?: string; uri?: string; error?: string }> =>
    (await apiClient.post('/rpc/mfa_enrolar_iniciar', { _ticket: ticket })).data,
  mfaEnrolarConfirmar: async (ticket: string, codigo: string): Promise<MfaRespuesta> => {
    const { data } = await apiClient.post('/rpc/mfa_enrolar_confirmar', { _ticket: ticket, _codigo: codigo });
    if (data?.token) tokenStore.set(data.token, data.refreshToken, recordarPendiente);
    return data;
  },

  /**
   * Cambia la propia clave → /rpc/cambiar_mi_clave (schema autenticacion).
   */
  cambiarClave: async (actual: string, nueva: string) => {
    const { data } = await apiClient.post('/rpc/cambiar_mi_clave', { _actual: actual, _nueva: nueva });
    return data;
  },

  /**
   * Registro → /rpc/register
   */
  register: async (registerData: RegisterData) => {
    const { data } = await apiClient.post('/rpc/register', {
      _username: registerData.username,
      _email: registerData.email,
      _password: registerData.password,
      _empresa_id: registerData.empresaId,
    });
    return data;
  },

  /**
   * Logout: limpia los tokens locales (stateless en el servidor).
   */
  logout: async () => {
    tokenStore.clear();
    try { for (const k of ['erp_user_cache', 'erp_menus_cache']) { sessionStorage.removeItem(k); localStorage.removeItem(k); } } catch { /* n/a */ }
    return { message: 'Logout exitoso' };
  },

  /**
   * Renovar el access token con el refresh token → /rpc/refresh
   */
  refresh: async (): Promise<{ user: User; token: string }> => {
    const { data } = await apiClient.post('/rpc/refresh', {
      _refresh_token: tokenStore.getRefresh(),
    });
    tokenStore.set(data.token, data.refreshToken);
    return data;
  },

  /**
   * Usuario actual → /rpc/me
   */
  getCurrentUser: async (): Promise<{ user: User }> => {
    const { data } = await apiClient.post('/rpc/me', {});
    return data;
  },

  /**
   * Branding de empresa por slug (login multi-tenant, Fase 1) → /rpc/login_branding.
   * Llamada anónima (sin token). Devuelve null si el slug no existe/no está activo.
   */
  getBranding: async (slug: string): Promise<LoginBranding | null> => {
    const { data } = await apiClient.post('/rpc/login_branding', { _slug: slug });
    return data ?? null;
  },

  /**
   * Branding de empresa por DOMINIO → /rpc/login_branding_host (script 904).
   * Llamada anónima. Devuelve null si este host no está asignado a ninguna
   * empresa activa, que es el caso normal en localhost o en una IP pelada.
   *
   * Nunca lanza: un entorno cuya BD aún no tenga la RPC (404) debe comportarse
   * igual que un dominio sin asignar, no romper la pantalla de login.
   */
  getBrandingHost: async (host: string): Promise<LoginBrandingHost | null> => {
    try {
      const { data } = await apiClient.post('/rpc/login_branding_host', { _host: host });
      return data && data.slug && data.nombre ? (data as LoginBrandingHost) : null;
    } catch {
      return null;
    }
  },

  /**
   * Solicita el enlace de recuperación de clave → /rpc/solicitar_reset_clave.
   * Llamada anónima (sin token). Respuesta SIEMPRE neutra ({ ok: true }): nunca
   * revela si el correo existe o no. `tenant` = slug de la empresa (requerido
   * por el backend para resolver a qué empresa pertenece el correo).
   */
  solicitarReset: async (email: string, tenant?: string | null): Promise<{ ok: boolean }> => {
    const { data } = await apiClient.post('/rpc/solicitar_reset_clave', {
      _email: email,
      _tenant: tenant ?? null,
    });
    return data;
  },

  /**
   * Aplica la clave nueva con el token del enlace de recuperación → /rpc/reset_clave.
   * Llamada anónima (sin token). Lanza error de PostgREST si el enlace es
   * inválido/expiró o si la clave no cumple el mínimo (usar getErrorMessage).
   */
  resetClave: async (token: string, nueva: string): Promise<{ ok: boolean }> => {
    const { data } = await apiClient.post('/rpc/reset_clave', { _token: token, _nueva: nueva });
    return data;
  },
};
